/**
 * @description Tenant auth navigation bar — shared between AuthLayout and loading states
 *
 * Key features:
 * - Tenant-branded nav with logo/platform name + Sign In / Sign Up links
 * - Dark-themed to match tenant auth visual system (bg-auth-bg)
 * - Glassmorphic backdrop blur consistent with AuthLayout
 * - Active route highlighting via current pathname
 *
 * @dependencies
 * - BrandingContext: Tenant branding (platformName, logoUrl)
 *
 * @notes
 * - Extracted from AuthLayout to allow reuse in SignIn/SignUp loading states
 *   without mounting the full AuthLayout (prevents remount glitch on OAuth redirect)
 * - Only rendered for tenants (!isDefault) — LynxPay uses App-level header
 * - Future: SO-customizable via CSS custom properties for white-label auth pages
 */

import { Link, useLocation } from 'react-router-dom';
import { useBranding } from '../../contexts/BrandingContext';
import { isSignupChromeAllowed } from '../../utils/authSignupChrome';

function AuthNav() {
  const { branding, signupEnabled } = useBranding();
  const { platformName } = branding.identity;
  const logoUrl = branding.assets.logoUrl;
  const location = useLocation();

  const isSignInRoute = location.pathname.startsWith('/sign-in');
  const isSignUpRoute = location.pathname.startsWith('/sign-up');

  return (
    <nav className="fixed top-0 left-0 right-0 z-20 border-b border-white/5 bg-auth-bg/80 backdrop-blur-xl">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <div className="flex-shrink-0 flex items-center mr-4 min-w-0">
          {logoUrl ? (
            <img src={logoUrl} alt={platformName} className="h-8 w-auto max-w-[140px] sm:max-w-[200px] object-contain" />
          ) : (
            <span className="text-brand font-bold tracking-tight text-lg sm:text-xl truncate">{platformName}</span>
          )}
        </div>
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <Link
            to="/sign-in"
            className={`rounded-xl px-3 sm:px-4 py-2 text-sm font-semibold transition-all ${
              isSignInRoute
                ? 'bg-brand text-auth-bg'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Sign In
          </Link>
          {isSignupChromeAllowed({ signupEnabled }) && (
            <Link
              to="/sign-up"
              className={`rounded-xl px-3 sm:px-4 py-2 text-sm font-semibold transition-all ${
                isSignUpRoute
                  ? 'bg-brand text-auth-bg'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Sign Up
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
}

export default AuthNav;
